import { Icon } from '@pypestream/design-system';
import { FC, useMemo } from 'react';

import { tableStore } from '../../stores/table-store';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { runPassed, summarizeRuns } from '../validate/validate-runs';
import { localValidateStore } from '../validate/validate-store';
import { asVersion, runsOnVersion } from './version-runs';

/**
 * What Validate says about the version this publish sends out, beside the
 * Publish it gates (prototype `.dp-valid`): the pass rate of the runs on that
 * version and its latest failure, or that it hasn't been tested. Opens
 * Validate.
 *
 * Validate's conversations reach whatever is published to Sandbox, never the
 * draft — so a draft is untested until it's published there, and only runs on
 * that version count for it.
 */
export const ValidationCallout: FC<{
  /** The published version going out; null when it's the draft. */
  version: string | null;
  /** What's in Sandbox now, which Validate is testing. */
  sandboxVersion: string | null;
}> = ({ version, sandboxVersion }) => {
  const solutionKey = useProStudioCtxSelector((ctx) =>
    ctx.customerId && ctx.botName ? `${ctx.customerId}.${ctx.botName}` : null
  );
  const allRuns = useMemo(
    () => (solutionKey ? localValidateStore.loadRuns(solutionKey) : []),
    [solutionKey]
  );
  const runs = useMemo(
    () => runsOnVersion(allRuns, version),
    [allRuns, version]
  );
  const summary = summarizeRuns(runs);
  const latestFailure = runs.find((r) => runPassed(r) === false);
  const openValidate = () => tableStore.setActiveStage('validate');

  if (!version) {
    return (
      <section
        className="c-deploy-valid c-deploy-valid--none"
        data-testid="bobcat-deploy-validation"
      >
        <Icon name="info" size="xsmall" />
        <div>
          <b>The draft hasn’t been tested</b>
          <span>
            Validate tests what’s published to Sandbox
            {sandboxVersion ? ` (${asVersion(sandboxVersion)} now)` : ''}, not
            the draft. Publish it to Sandbox, test it there, then promote it to
            Live.
          </span>
        </div>
        <button
          type="button"
          className="c-deploy-valid__open"
          onClick={openValidate}
        >
          Validate
        </button>
      </section>
    );
  }

  if (!summary.scored) {
    return (
      <section
        className="c-deploy-valid c-deploy-valid--none"
        data-testid="bobcat-deploy-validation"
      >
        <Icon name="info" size="xsmall" />
        <div>
          <b>{asVersion(version)} not validated yet</b>
          <span>
            {runs.length
              ? `Runs on ${asVersion(version)} are still being scored.`
              : `No runs on ${asVersion(version)} in Sandbox. Run its suites before it goes live.`}
          </span>
        </div>
        <button
          type="button"
          className="c-deploy-valid__open"
          onClick={openValidate}
        >
          Validate
        </button>
      </section>
    );
  }

  return (
    <section
      className={`c-deploy-valid${summary.failed ? ' c-deploy-valid--failing' : ''}`}
      data-testid="bobcat-deploy-validation"
    >
      <Icon name={summary.failed ? 'warning' : 'check'} size="xsmall" />
      <div>
        <b>
          {asVersion(version)} validated · {summary.successRate}% of{' '}
          {summary.scored} run
          {summary.scored === 1 ? '' : 's'} passed
        </b>
        {latestFailure && (
          <span>
            {summary.failed} open failure{summary.failed === 1 ? '' : 's'}:
            {` “${latestFailure.title}”`}
          </span>
        )}
      </div>
      <button
        type="button"
        className="c-deploy-valid__open"
        onClick={openValidate}
      >
        Open
      </button>
    </section>
  );
};
